import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { G_DRAG_AND_DROP__area_with_label } from '@/pages/page_generation/layouts/nodes/node/fields/image/_base/_drag_and_drop/G_DRAG_AND_DROP__area_with_label';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { REMOTE__collection_image_add } from '@/state/collection/cell/REMOTE__collection_image_add';
import { LOCAL__ui_set_boolean } from '@/state/ui/_base/LOCAL__ui_set_boolean';
import { LOCAL__ui_drag_and_drop_set_image_dragged } from '@/state/ui/drag_and_drop/LOCAL__ui_drag_and_drop_set_image_dragged';
import { assert_false } from '@shared/asserts/utils_asserts';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { DTO_REQUEST__collection_image_add } from '@shared/dto/collection/_requests/DTO_REQUEST__collection_image_add';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DragEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  entity: DTO__entity_with_grid2d;
  grid2d: DTO__grid2d;
}

export function DRAG_AND_DROP__grid2d_internal({ entity, grid2d }: Props) {
  const dispatch = use_dispatch();

  const { t: t_base_ui } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const { toast } = useToast();

  const { drag_and_drop__image_internal_zones_show, drag_and_drop__image_internal_dragged } = use_slice_ui([
    'drag_and_drop__image_internal_zones_show',
    'drag_and_drop__image_internal_dragged',
  ]);

  const is_image_with_similar_content_dragged = useMemo(() => get_is_image_with_similar_content_dragged(), [grid2d, drag_and_drop__image_internal_dragged]);

  /*
   *
   */

  /*
   * EVENTS
   */

  function onDragOver(e: DragEvent) {
    e.preventDefault();
  }

  async function onDrop() {
    if (drag_and_drop__image_internal_dragged.is_stub()) return;

    if (!is_interactive()) return;

    assert_false(drag_and_drop__image_internal_dragged.is_stub());

    let request = new DTO_REQUEST__collection_image_add();
    request.hash_sha256_content = drag_and_drop__image_internal_dragged.hash_sha256_content;
    request.collection_id = entity._id;

    dispatch(LOCAL__ui_set_boolean({ key: 'drag_and_drop__image_internal_zones_show', value: false }));
    dispatch(LOCAL__ui_drag_and_drop_set_image_dragged());

    await dispatch(REMOTE__collection_image_add([request]));
  }

  /*
   *
   */

  function get_class_name_opacity() {
    return drag_and_drop__image_internal_zones_show ? ' opacity-100 ' : ' opacity-0 ';
  }

  function is_interactive() {
    if (drag_and_drop__image_internal_dragged.is_stub()) return false;

    let out = true;

    if (!drag_and_drop__image_internal_zones_show) {
      out = false;
    } else if (!entity.is_add_internal_image_possible()) {
      out = false;
    } else if (is_image_with_similar_content_dragged) {
      out = false;
    }

    return out;
  }

  function get_text() {
    let out = '';

    if (drag_and_drop__image_internal_dragged.is_stub()) {
      out = 'No image to drop';
    } else if (!entity.is_add_internal_image_possible()) {
      out = 'Not for internal images';
    } else if (is_image_with_similar_content_dragged) {
      out = 'Same Image is Already Here';
    } else {
      out = 'Drop Image Here';
    }

    return out;
  }

  function get_is_image_with_similar_content_dragged() {
    if (grid2d.is_stub()) return false;

    let { hash_sha256_content } = drag_and_drop__image_internal_dragged;

    let out = grid2d.cells__get_loaded().some(cell => cell.hash_sha256_content == hash_sha256_content);

    return out;
  }

  /*
   * RENDERS
   */

  /*
   * HTML
   */

  if (entity.is_stub()) return null;

  return (
    <div
      {...{ onDragOver, onDrop }}
      className={`absolute inset-0 flex-center 
        bg-black/80
        
    ${is_interactive() ? ' border border-2 border-dashed border-component-item-border  ' : ' pointer-events-none '}
    ${get_class_name_opacity()}
    
    `}
    >
      <G_DRAG_AND_DROP__area_with_label text={get_text()} class_name="bg-white/10" />
    </div>
  );
}
